'use client';

import {useEffect, useState} from 'react';
import Link from '@/components/Link';
import {AnimatePresence, motion} from 'motion/react';
import {ArrowRight, Menu, X} from 'lucide-react';
import {NAV_LINKS, WHATSAPP_LINK} from '@/lib/landing/constants';

const SCROLL_THRESHOLD = 20;

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
    handleScroll();
    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      aria-label="Navegación principal"
      className={`fixed top-0 w-full z-50 backdrop-blur-md border-b transition-all duration-300 ${
        isScrolled
          ? 'bg-kobra-dark/95 border-kobra-border-dark py-3'
          : 'bg-transparent border-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link href="/" className="flex items-center shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kobra-accent rounded-lg">
            <span className="relative block h-12 w-44 overflow-hidden sm:h-14 sm:w-52 md:h-16 md:w-60">
              <img
                src={isScrolled ? '/image/logoBlanco.png' : '/image/logo.png'}
                alt="Kobra"
                className="absolute left-0 top-1/2 h-[240%] w-auto max-w-none -translate-y-1/2"
              />
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`text-sm font-medium transition-colors hover:text-kobra-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kobra-accent rounded ${
                  isScrolled ? 'text-kobra-text-muted-light' : 'text-kobra-text-muted'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="#docs"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kobra-accent ${
                isScrolled ? 'text-kobra-text-muted-light hover:text-white' : 'text-kobra-text-muted hover:text-kobra-text-dark'
              }`}
            >
              Ver documentación
            </Link>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className={`bg-kobra-accent hover:bg-kobra-accent-hover text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kobra-accent focus-visible:ring-offset-2 ${
                isScrolled ? 'focus-visible:ring-offset-kobra-dark' : 'focus-visible:ring-offset-white'
              }`}
            >
              Solicitar demo
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>

          <button
            type="button"
            className={`lg:hidden p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kobra-accent rounded-lg ${
              isScrolled ? 'text-white hover:text-kobra-accent' : 'text-kobra-text-dark hover:text-kobra-accent'
            }`}
            onClick={() => setIsOpen((prev) => !prev)}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            id="mobile-menu"
            initial={{opacity: 0, y: -10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -10}}
            transition={{duration: 0.25, ease: [0.16, 1, 0.3, 1]}}
            className={`lg:hidden absolute top-full left-0 w-full border-b shadow-2xl px-4 pb-6 ${
              isScrolled ? 'bg-kobra-surface border-kobra-border-dark' : 'bg-white border-kobra-border'
            }`}
          >
            <div className="flex flex-col gap-3 pt-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`font-medium p-3 rounded-lg transition-colors ${
                    isScrolled
                      ? 'text-kobra-text-muted-light hover:bg-kobra-surface-elevated hover:text-white'
                      : 'text-kobra-text-muted hover:bg-kobra-bg hover:text-kobra-text-dark'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <hr className={isScrolled ? 'border-kobra-border-dark' : 'border-kobra-border'} />
              <Link
                href="#docs"
                onClick={() => setIsOpen(false)}
                className={`font-medium p-3 rounded-lg transition-colors ${
                  isScrolled
                    ? 'text-kobra-text-muted-light hover:bg-kobra-surface-elevated hover:text-white'
                    : 'text-kobra-text-muted hover:bg-kobra-bg hover:text-kobra-text-dark'
                }`}
              >
                Ver documentación
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="mt-2 bg-kobra-accent text-white px-4 py-3 rounded-xl font-medium text-center flex items-center justify-center gap-2 transition-colors hover:bg-kobra-accent-hover"
              >
                Solicitar demo
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </nav>
  );
}
